import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { ProductionTBSData } from './types';
import { getSourceColor, getSupplierColor } from './colors';
import { Box, Heading, useColorModeValue, useBreakpointValue } from '@chakra-ui/react';
import { formatNumber, formatPercentageNumber } from './utils';

interface PersentaseTBSProps {
  data: ProductionTBSData[];
}

const CustomTooltip = ({
  active,
  payload,
  bg,
  borderColor,
}: {
  active?: boolean;
  payload?: any;
  bg: string;
  borderColor: string;
}) => {
  if (active && payload?.length) {
    const d = payload[0].payload;
    return (
      <Box bg={bg} borderWidth={1} borderColor={borderColor} p={2} borderRadius="md" fontSize="sm">
        <div><b>{d.name}</b></div>
        <div>Berat: {formatNumber(d.value)} kg</div>
        {d.source && <div>Sumber: {d.source}</div>}
      </Box>
    );
  }
  return null;
};

const PersentaseTBS: React.FC<PersentaseTBSProps> = ({ data }) => {
  // data01: agregasi per source (Internal/Eksternal)
  const data01 = React.useMemo(() => {
    const agg: { [key: string]: number } = {};
    data.forEach(item => {
      agg[item.source] = (agg[item.source] || 0) + item.net_weight;
    });
    return Object.entries(agg).map(([name, value]) => ({ name, value }));
  }, [data]);

  // Hitung total di luar fungsi label
  const totalData01 = React.useMemo(() => {
    return data01.reduce((sum, entry) => sum + entry.value, 0);
  }, [data01]);

  // data02: per supplier
  const data02 = React.useMemo(() => {
    return data.map(item => ({
      name: item.supplier,
      value: item.net_weight,
      source: item.source,
    }));
  }, [data]);

  // Chakra UI color mode values
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.800', 'white');

  // Responsive values
  const outerRadius = useBreakpointValue({ 
    base: 60,      // mobile - smaller
    md: 80,        // tablet
    lg: 45,        // laptop
    xl: 55,        // desktop
    '2xl': 70      // large desktop
  }) || 70;

  const innerRadius = useBreakpointValue({ 
    base: 60,      // mobile - smaller
    md: 80,        // tablet
    lg: 45,        // laptop
    xl: 55,        // desktop
    '2xl': 70      // large desktop
  }) || 50;
  
  const fontSize = useBreakpointValue({ 
    base: 8,       // mobile - smaller
    md: 10,        // tablet
    lg: 13,        // laptop
    xl: 12,        // desktop
    '2xl': 14      // large desktop
  }) || 12;
  
  const headingSize = useBreakpointValue({ 
    base: 'sm',    // mobile
    md: 'sm',      // tablet
    lg: 'md',      // laptop
    xl: 'md',      // desktop
    '2xl': 'lg'    // large desktop
  }) || 'md';
  
  const padding = useBreakpointValue({ 
    base: 3,       // mobile
    md: 3,         // tablet
    lg: 4,         // laptop
    xl: 4,         // desktop
    '2xl': 5       // large desktop
  }) || 4;
  
  const marginBottom = useBreakpointValue({ 
    base: 2,       // mobile
    md: 2,         // tablet
    lg: 2,         // laptop
    xl: 2,         // desktop
    '2xl': 2       // large desktop
  }) || 2;

  // const renderLabel = React.useCallback((props: any) => {
  //   const { value, cx, cy, midAngle, outerRadius, name } = props;
  //   const RADIAN = Math.PI / 180;
  //   const percent = totalData01 ? ((value / totalData01) * 100).toFixed(1) : 0;

  //   // const radius = outerRadius - 60;
  //   const radius = innerRadius + (outerRadius - innerRadius) - 25;
  //   const x = cx + radius * Math.cos(-midAngle * RADIAN);
  //   const y = cy + radius * Math.sin(-midAngle * RADIAN);

  //   return (
  //     <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={fontSize} fontWeight="bold">
  //       {`${percent}%`}
  //     </text>
  //   );
  // }, [totalData01, fontSize]);

  const supplierLabelFontSize = useBreakpointValue({ 
    base: 12,       // mobile - smaller
    md: 12,         // tablet
    lg: 12,        // laptop
    xl: 12,        // desktop
    '2xl': 14      // large desktop
  }) || 10;

  const radiusSize = useBreakpointValue({ 
    base: 40,       // mobile - smaller
    md: 40,         // tablet
    lg: 25,        // laptop
    xl: 25,        // desktop
    '2xl': 45      // large desktop
  }) || 10;

  const renderLabel = React.useCallback((props: any) => {
    const { value, cx, cy, midAngle, outerRadius, name } = props;
    const RADIAN = Math.PI / 180;
    const percent = totalData01 ? ((value / totalData01) * 100).toFixed(1) : 0;

    const radius = innerRadius + (outerRadius - innerRadius) - radiusSize;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={supplierLabelFontSize}
        fontWeight="bold"
      >
        <tspan x={x} dy="-0.5em">{name}</tspan>
        <tspan x={x} dy="1.2em">{`${percent}%`}</tspan>
      </text>
    );
  }, [totalData01, fontSize, innerRadius, supplierLabelFontSize, radiusSize]);

  // Responsive minHeight for chart container
  const chartMinHeight = useBreakpointValue({
    base: '320px', // mobile
    md: '320px',   // tablet
    lg: '380px',   // laptop
    xl: '310px',   // desktop
    '2xl': '350px' // large desktop
  }) || '320px';

  const rowHeight = useBreakpointValue({ 
        base: '100%',     // mobile - smaller
        md: '100%',       // tablet
        lg: '100%',       // laptop
        xl: '80%',       // desktop
        '2xl': '90%'    // large desktop
    });

  return (
    <Box bg={bg} borderRadius="lg" p={4} h="100%" display="flex" flexDirection="column">
      <Heading as="h3" size={headingSize} mb={marginBottom} color={headingColor} fontSize="18px">
        PERSENTASE PRODUKSI TBS
      </Heading>
      <Box minH={chartMinHeight} h={chartMinHeight} w="100%">
        <ResponsiveContainer width="100%" height={rowHeight}>
          <PieChart>
            <Pie
              data={data01}
              dataKey="value"
              cx="50%"
              cy="50%"
              outerRadius={outerRadius}
              fill="#8884d8"
              nameKey="name"
              isAnimationActive={false}
              label={renderLabel}
              labelLine={false}
            >
              {data01.map((entry) => (
                <Cell key={entry.name} fill={getSourceColor ? getSourceColor(entry.name) : "#8884d8"} />
              ))}
            </Pie>
            <Pie
              data={data02}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius + 20}
              fill="#82ca9d"
              nameKey="name"
              isAnimationActive={false}
              labelLine={true}
              label={(props) => {
                const { value } = props;
                const total = data02.reduce((sum, entry) => sum + entry.value, 0);
                const percent = total ? formatPercentageNumber((value / total) * 100, 1) : '0,0';
                return `${props.name} ${percent}%`;
              }}
              style={{ fontSize: supplierLabelFontSize }}
            >
              {data02.map((entry) => (
                <Cell key={entry.name} fill={getSupplierColor ? getSupplierColor(entry.name) : "#82ca9d"} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip bg={bg} borderColor={borderColor} />} />
            {/* <Legend 
              payload={data01.map((entry) => ({
                value: entry.name,
                type: 'square',
                color: getSourceColor ? getSourceColor(entry.name) : "#8884d8"
              }))}
            /> */}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default PersentaseTBS;